import {
  Button,
  ButtonGroup,
  Popover,
  useDisclosure,
  RadioGroup,
  Stack,
  Text,
  NativeSelect,
  Icon,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import {
  CraAttributes,
  CraAttributeName,
  CrashType,
  TreatmentType,
} from "@keita/lanepatrol-sdk";
import { CraColoring, CraColoringType } from "../../../../map/models/CraColoring";
import { tScoreType } from "../../../../coding/utils/score-utils";
import { CraAttributeUtils } from "../../../../coding/utils/attribute-utils";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { ChevronDownIcon } from "../../../../shared/views/icons";

const crashTypes = Object.values(CrashType);
const craColoringTypes = Object.values(CraColoringType);
const treatmentTypes = Object.values(TreatmentType);
const defaultScore = "global";
const defaultTreatment =
  TreatmentType.UpgradeExistingFacilityToAnOnRoadBicycleLaneWithLightSegregation;
const defaultAtribute = CraAttributes.all.at(0)!;

interface Props {
  value: CraColoring;
  onChange: (craColoring: CraColoring) => void;
}
export const CraColoringSelector: React.FC<Props> = ({ value, onChange }) => {
  const popover = useDisclosure();
  const [craColoring, setCraColoring] = useState(value);

  useEffect(() => {
    if (popover.open) setCraColoring(value);
    //eslint-disable-next-line
  }, [popover.open]);

  const changeType = (type: CraColoringType) => {
    if (CraColoringType.Score === type) {
      setCraColoring({ type, scoreKey: defaultScore });
    } else if (CraColoringType.Treatment === type) {
      setCraColoring({ type, treatmentType: defaultTreatment });
    } else if (CraColoringType.Attribute === type) {
      setCraColoring({ type, attributeName: defaultAtribute.name });
    }
  };

  const submit = () => {
    onChange(craColoring);
    popover.onClose();
  };

  return (
    <CustomPopover
      open={popover.open}
      trigger={
        <Button
          colorPalette="primary"
          fontSize={"sm"}
          size="sm"
          variant="plain"
          onClick={popover.onToggle}
        >
          <Text lineClamp={1} whiteSpace="pre-line">
            {CraColoringType.Score === value.type && tScoreType(value.scoreKey)}
            {CraColoringType.Treatment === value.type &&
              t(`sdk:treatments.${value.treatmentType}`)}
            {CraColoringType.Attribute === value.type &&
              CraAttributeUtils.getLabel(value.attributeName)}
          </Text>
          <Icon as={ChevronDownIcon} />
        </Button>
      }
      onClose={popover.onClose}
      width={400}
      maxH="360px"
    >
      {popover.open && (
        <>
          <Popover.Header>{t("map:header.lineColor")}</Popover.Header>
          <Popover.Body overflowY="auto">
            <NativeSelect.Root mb={2}>
              <NativeSelect.Field
                onChange={(event) =>
                  changeType(event.target.value as CraColoringType)
                }
                value={craColoring.type}
              >
                {craColoringTypes.map((type, i) => (
                  <option value={type} key={i}>
                    {t(`map:craColoring.type.${type}`)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            {CraColoringType.Score === craColoring.type && (
              <RadioGroup.Root
                py={2}
                value={craColoring.scoreKey}
                onValueChange={({ value }) => {
                  setCraColoring({
                    type: CraColoringType.Score,
                    scoreKey: value as CrashType,
                  });
                }}
                size="sm"
                colorPalette={"primary"}
              >
                <Stack>
                  <RadioGroup.Item key="global" value="global">
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemIndicator />
                    <RadioGroup.ItemText>
                      {tScoreType("global")}
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                  {crashTypes.map((key, i) => (
                    <RadioGroup.Item key={i} value={key}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {tScoreType(key)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {CraColoringType.Treatment === craColoring.type && (
              <RadioGroup.Root
                py={2}
                size="sm"
                colorPalette={"primary"}
                value={craColoring.treatmentType}
                onValueChange={({ value }) => {
                  setCraColoring({
                    type: CraColoringType.Treatment,
                    treatmentType: value as TreatmentType,
                  });
                }}
              >
                <Stack>
                  {treatmentTypes.map((type, i) => (
                    <RadioGroup.Item key={i} value={type}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {t(`sdk:treatments.${type}`)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {CraColoringType.Attribute === craColoring.type && (
              <RadioGroup.Root
                py={2}
                value={craColoring.attributeName}
                size="sm"
                colorPalette={"primary"}
                onValueChange={({ value }) => {
                  setCraColoring({
                    type: CraColoringType.Attribute,
                    attributeName: value as CraAttributeName,
                  });
                }}
              >
                <Stack>
                  {CraAttributes.all.map((attribute, i) => (
                    <RadioGroup.Item key={i} value={`${attribute.name}`}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemText>
                        {CraAttributeUtils.getLabel(attribute.name)}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}
          </Popover.Body>
          <Popover.Footer display="flex" justifyContent="flex-end">
            <ButtonGroup colorPalette={"primary"} size={"sm"}>
              <Button onClick={submit}>{t("map:filters.submitButton")}</Button>
            </ButtonGroup>
          </Popover.Footer>
        </>
      )}
    </CustomPopover>
  );
};
